import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { sortCategoriesByOrder } from "../lib/categoryOrder";
import { optimizeImageUrl } from "../lib/servicesCache";

/**
 * Horizontal carousel showing the TOP service from each category.
 * "Top" = lowest display_order (admin-configurable). When tied, first in DB order.
 *
 * Each slide is a square-image card with category overline, name, short description,
 * price (or "from S$X" when variants exist), and a Book Now CTA that pre-selects it.
 */
export default function ServicesSlider({ services = [], categoryOrder = [], showDuration = true, title = "Top picks", subtitle = "One signature service from every category." }) {
    // Pick the top service per category (lowest display_order)
    const featured = useMemo(() => {
        const buckets = services.filter((s) => s.featured !== false).reduce((acc, s) => {
            (acc[s.category] = acc[s.category] || []).push(s);
            return acc;
        }, {});
        const ordered = sortCategoriesByOrder(Object.entries(buckets), categoryOrder);
        return ordered.map(([cat, list]) => {
            const sorted = [...list].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
            return { ...sorted[0], _category_label: cat };
        });
    }, [services, categoryOrder]);

    const trackRef = useRef(null);
    const containerRef = useRef(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isVisible, setIsVisible] = useState(true);

    const updateArrows = () => {
        const el = trackRef.current;
        if (!el) return;
        setCanPrev(el.scrollLeft > 4);
        setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    useEffect(() => {
        updateArrows();
        const el = trackRef.current;
        if (!el) return;
        el.addEventListener("scroll", updateArrows, { passive: true });
        const ro = new ResizeObserver(updateArrows);
        ro.observe(el);
        return () => {
            el.removeEventListener("scroll", updateArrows);
            ro.disconnect();
        };
    }, [featured.length]);

    // Pause autoplay when slider is off-screen
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.25 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const scrollBy = (dir) => {
        const el = trackRef.current;
        if (!el) return;
        const cardWidth = Math.max(280, Math.min(el.clientWidth / 3, 380));
        el.scrollBy({ left: dir * (cardWidth + 16), behavior: "smooth" });
    };

    // Auto-advance every 5s; loop to start at end. Respects reduced-motion, hover, visibility.
    useEffect(() => {
        if (featured.length <= 1) return;
        const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduce || isHovered || !isVisible) return;
        const timer = setInterval(() => {
            const el = trackRef.current;
            if (!el) return;
            const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
            if (atEnd) {
                el.scrollTo({ left: 0, behavior: "smooth" });
            } else {
                scrollBy(1);
            }
        }, 5000);
        return () => clearInterval(timer);
    }, [featured.length, isHovered, isVisible]);

    if (featured.length === 0) return null;

    return (
        <section
            ref={containerRef}
            className="relative"
            data-testid="services-slider"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocusCapture={() => setIsHovered(true)}
            onBlurCapture={() => setIsHovered(false)}
        >
            <div className="flex items-end justify-between gap-6 mb-8">
                <div>
                    <div className="text-overline">{title}</div>
                    <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3D0C1F] mt-2">{subtitle}</h2>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => scrollBy(-1)}
                        disabled={!canPrev}
                        className="w-11 h-11 border-2 border-[#C29A5B]/60 text-[#8F622C] flex items-center justify-center hover:bg-[#6B1535] hover:text-[#FFF8F2] hover:border-[#C29A5B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid="services-slider-prev"
                        aria-label="Previous"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollBy(1)}
                        disabled={!canNext}
                        className="w-11 h-11 border-2 border-[#C29A5B]/60 text-[#8F622C] flex items-center justify-center hover:bg-[#6B1535] hover:text-[#FFF8F2] hover:border-[#C29A5B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid="services-slider-next"
                        aria-label="Next"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            </div>

            <div
                ref={trackRef}
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-1 px-1 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
                data-testid="services-slider-track"
            >
                {featured.map((s) => {
                    const variants = s.variants || [];
                    const priceLabel = variants.length > 0
                        ? `from S$${Math.min(...variants.map((v) => Number(v.price)))}`
                        : `S$${s.price}`;
                    const op = Number(s.original_price) || 0;
                    const priceNum = Number(s.price) || 0;
                    const hasDiscount = variants.length === 0 && op > priceNum && priceNum > 0;
                    const pct = hasDiscount ? Math.round(((op - priceNum) / op) * 100) : 0;
                    return (
                        <article
                            key={s.id}
                            className="snap-start flex-shrink-0 w-[220px] sm:w-[240px] lg:w-[260px] card-luxe grain group flex flex-col"
                            data-testid={`slider-card-${s.id}`}
                        >
                            {s.image ? (
                                <div className="aspect-[16/9] overflow-hidden relative">
                                    <img src={optimizeImageUrl(s.image, 320)} alt={s.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    {hasDiscount && (
                                        <div className="absolute top-2 right-2 bg-[#6B1535] text-[#FFF8F2] px-2 py-0.5 text-[0.55rem] tracking-[0.2em] uppercase font-semibold" data-testid={`slider-discount-badge-${s.id}`}>-{pct}% OFF</div>
                                    )}
                                </div>
                            ) : (
                                <div className="aspect-[16/9] bg-gradient-to-br from-[#FDF0EC] to-[#FBF6F0] flex items-center justify-center relative">
                                    <span className="font-serif-display text-[#8F622C]/30 text-3xl">{s.name?.[0] || "T"}</span>
                                    {hasDiscount && (
                                        <div className="absolute top-2 right-2 bg-[#6B1535] text-[#FFF8F2] px-2 py-0.5 text-[0.55rem] tracking-[0.2em] uppercase font-semibold" data-testid={`slider-discount-badge-${s.id}`}>-{pct}% OFF</div>
                                    )}
                                </div>
                            )}
                            <div className="p-3 flex-1 flex flex-col">
                                <div className="text-[0.55rem] tracking-[0.2em] uppercase text-[#7A5262]">{s._category_label}</div>
                                <h3 className="font-serif-display text-base leading-tight text-[#3D0C1F] mt-1 line-clamp-1">{s.name}</h3>
                                <p className="text-[0.7rem] text-[#8A6574] mt-1 line-clamp-2 leading-snug flex-1">{s.description}</p>
                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#6B1535]/10">
                                    <div className="flex items-baseline gap-2">
                                        <div className="text-[#8F622C] text-sm font-bold">{priceLabel}</div>
                                        {hasDiscount && (
                                            <div className="text-[0.65rem] text-[#8A6574] line-through">S${op}</div>
                                        )}
                                    </div>
                                    {s.duration_min != null && showDuration && (
                                        <div className="text-[0.65rem] text-[#8A6574]">{s.duration_min} min</div>
                                    )}
                                </div>
                                <Link
                                    to={`/book?service=${s.id}`}
                                    className="btn-gold mt-2 justify-center py-1.5 text-[0.65rem] font-bold tracking-[0.2em]"
                                    data-testid={`slider-book-${s.id}`}
                                >
                                    Book Now <ArrowUpRight size={10} />
                                </Link>
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}
